/**
 * Diálogo de confirmação padrão do software.
 *
 * Substitui `window.confirm` em toda a aplicação. Suporta tons (primary,
 * danger) e estado `busy` para ações assíncronas. Acessível por teclado:
 * Esc cancela, Enter confirma (quando não está digitando em um input).
 */
import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Actions,
  Button,
  Message,
  Modal,
  Overlay,
  Spinner,
  Title,
} from './ConfirmDialog.styles';

export type ConfirmDialogTone = 'primary' | 'danger';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmDialogTone;
  busy?: boolean;
  showCancel?: boolean;
  showConfirm?: boolean;
  confirmDisabled?: boolean;
  /** Renderização dentro de outro portal que já controla foco (ex.: Radix). */
  portal?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'primary',
  busy = false,
  showCancel = true,
  showConfirm = true,
  confirmDisabled = false,
  portal = true,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element | null {
  const titleId = useId();
  const descId = useId();
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  // Foca no botão Cancelar quando abre (segurança em exclusões: evita
  // confirmar por acidente). Para ações destrutivas, manter o foco no
  // botão menos perigoso é a boa prática.
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      if (showCancel) {
        cancelRef.current?.focus();
      } else {
        confirmRef.current?.focus();
      }
    }, 0);
    return () => {
      window.clearTimeout(t);
    };
  }, [open, showCancel]);

  const handleKey = useCallback(
    (event: KeyboardEvent) => {
      if (!open || busy) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      } else if (
        event.key === 'Enter' &&
        showConfirm &&
        !confirmDisabled
      ) {
        const target = event.target as HTMLElement | null;
        const tag = target?.tagName;
        if (tag === 'TEXTAREA') return;
        if (tag === 'INPUT' && target instanceof HTMLInputElement && target.type !== 'button') {
          return;
        }
        event.preventDefault();
        void onConfirm();
      }
    },
    [open, busy, confirmDisabled, onCancel, onConfirm, showConfirm],
  );

  useEffect(() => {
    if (!open) return;
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('keydown', handleKey);
    };
  }, [open, handleKey]);

  if (!open) return null;

  const node = (
    <Overlay
      role="presentation"
      onClick={() => {
        if (!busy) onCancel();
      }}
    >
      <Modal
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        aria-busy={busy}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <Title id={titleId}>{title}</Title>
        <Message id={descId}>{message}</Message>
        <Actions>
          {showCancel ? (
            <Button
              ref={cancelRef}
              type="button"
              $tone="cancel"
              disabled={busy}
              onClick={onCancel}
            >
              {cancelLabel}
            </Button>
          ) : null}
          {showConfirm ? (
            <Button
              ref={confirmRef}
              type="button"
              $tone={tone === 'danger' ? 'danger' : 'primary'}
              disabled={busy || confirmDisabled}
              onClick={() => {
                void onConfirm();
              }}
            >
              {busy && <Spinner aria-hidden="true" />}
              {confirmLabel}
            </Button>
          ) : null}
        </Actions>
      </Modal>
    </Overlay>
  );

  return portal ? createPortal(node, document.body) : node;
}
